import {
  FormField,
  Input,
  KeyValuePairs,
  SpaceBetween,
  Toggle,
} from "@amzn/awsui-components-console";
import { useEffect, useRef, useState } from "react";

import { useChatHistory } from "@/contexts/ChatHistoryContext";
import { validateText } from "@/utils/utils";

import useModal from "../useModal";

export interface UseRenameModalProps {
  chatId: string;
  title: string;
  isPinned: boolean;
}

const useRenameModal = ({ chatId, title, isPinned }: UseRenameModalProps) => {
  const { loading, updateChatTitle } = useChatHistory();
  const [newTitle, setNewTitle] = useState(title);
  const [pinned, setPinned] = useState(isPinned);
  const [touched, setTouched] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const { isValid, message: errorMessage } = validateText(newTitle);

  useEffect(() => {
    if (title) {
      setNewTitle(title);
    }
  }, [title]);

  const handleRename = () => {
    if (isValid) {
      updateChatTitle(chatId, newTitle, pinned);
      setNewTitle("");
    }
  };

  const { visible, ...rest } = useModal({
    title: "Rename Chat",
    onConfirm: handleRename,
    loading,
    disabled: !isValid,
    content: (
      <SpaceBetween size="s">
        <KeyValuePairs
          items={[
            {
              label: "Original Title",
              value: title,
            },
          ]}
        />

        <FormField
          label="New Title"
          errorText={touched && !isValid ? errorMessage : undefined}
        >
          <Input
            ref={inputRef}
            value={newTitle}
            onChange={(e) => setNewTitle(e.detail.value)}
          />
        </FormField>
        <FormField description="You can also toggle to pin this chat above your chat history.">
          <Toggle
            onChange={({ detail }) => setPinned(detail.checked)}
            checked={pinned}
          >
            Pin Chat
          </Toggle>
        </FormField>
      </SpaceBetween>
    ),
  });

  useEffect(() => {
    // Reset.
    if (!visible) {
      setNewTitle("");
      setTouched(false);
    } else {
      inputRef.current?.focus();
    }
  }, [visible]);

  return rest;
};

export default useRenameModal;
